import { watch } from "vue";
import { storeToRefs } from "pinia";
import { useIServiceCategoryRootNewApi } from "@/hooks/api/useIServiceApi";
import { useGetDivision } from "@/hooks/form-v3/useIServiceDivisionCode";
import { __CMP_MODEL_NUMBER__ } from "@/const/csrCmpName";
import { __DATA_FROM_ISERVICE__ } from "@/const/iserviceConst";
import { toBool } from "@/utils/tools";

/**
 * category 组件的 iService 数据源逻辑
 * 仅当 state.conf.dataFrom === __DATA_FROM_ISERVICE__ 时生效，包含：
 * 1. loadIServiceCategory：加载 category 选项（categoryRootNew 接口），由组件在 onMounted 中自行调用
 * 2. 联动 model-number：监听 store.selectedModel 自动选中对应品类（此时 category 始终禁用）
 * @param {Object} options
 * @param {Object} options.store - csr pinia store，读取 selectedModel
 * @param {Object} options.state - 当前组件的 csr state（含 conf.dataFrom/conf.serviceType/conf.parentFieldName/conf.enableLinkage）
 * @param {Object} options.currentActionState - 当前表单提交按钮的 state（用于获取 divisionCode）
 * @returns {{ isIService: boolean, loadIServiceCategory: Function }}
 */
export const useIServiceCategory = ({ store, state, currentActionState }) => {
  const isIService = state.conf?.dataFrom === __DATA_FROM_ISERVICE__;

  // 加载category选项（仅iService数据源时执行，由组件在onMounted中调用）
  const loadIServiceCategory = async () => {
    if (!isIService) {
      return;
    }
    const { divisionCode } = await useGetDivision(currentActionState);
    const { data } = await useIServiceCategoryRootNewApi({
      divisionCode,
      serviceType: state.conf.serviceType,
    });
    state.conf = {
      ...state.conf,
      dataSource: (data.value || []).map(item => ({
        label: item.englishName,
        value: item.englishName,
        ...item,
      })),
    };
  };

  if (isIService) {
    const parentFieldName = state.conf?.parentFieldName;

    // 联动model-number：监听selectedModel变化；model-number 联动时 category 始终禁用（自动选中，只读）
    if (toBool(state.conf?.enableLinkage) && parentFieldName === __CMP_MODEL_NUMBER__) {
      // 始终禁用，仅需设置一次，避免每次 watch 回调重建 conf
      state.conf = { ...state.conf, disabled: true };
      const selectedModel = storeToRefs(store).selectedModel;
      watch(selectedModel, (model) => {
        state.value = null;
        const buId = model?.buId;
        if (!buId) {
          return;
        }
        // 根据buId匹配categoryId并选中
        const matchedItem = state.conf?.dataSource?.find(item => item.categoryId === buId);
        if (matchedItem) {
          state.value = matchedItem.value;
        }
      }, { immediate: true });
    }
  }

  return {
    isIService,
    loadIServiceCategory,
  };
};
